import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, increment, runTransaction } from 'firebase/firestore';
import { getAuth, setPersistence, signInAnonymously, browserLocalPersistence } from 'firebase/auth';
import { format } from 'date-fns';
import { db } from './firebaseConfig';
import './GameUI.css';
import coffeeIcon from './assets/icons/bmc-logo-yellow.png';
import logo from './assets/icons/logo-no-background.png';
import facebookIcon from './assets/icons/facebook-brands-solid.svg';
import xIcon from './assets/icons/x-twitter-brands-solid.svg';
import linkIcon from './assets/icons/copy-regular.svg';
import statsIcon from './assets/icons/chart-simple-solid.svg';
import triviaIcon from './assets/icons/masks-theater-solid.svg';
import partyIcon from './assets/icons/confetti-bold.svg';
import sadIcon from './assets/icons/heart-crack-solid.svg';
import challengeIcon from './assets/icons/lightning-bold.svg';
import leaderboardIcon from './assets/icons/ranking-bold.svg';
import SideMenu from './SideMenu';
import FooterLinks from './FooterLinks';

const DAILY_FIVE_KEY = 'dailyFiveLastPlayedDate';
const DAILY_FIVE_SHARE_KEY = 'dailyFiveShareMessage';
const DAILY_FIVE_ATTEMPTS_KEY = 'dailyFiveAttempts';
const DAILY_FIVE_SCORE_KEY = 'dailyFiveScore';

function getTimeUntilMidnight() {
	const now = new Date();
	const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
	const diff = midnight - now;
	if (diff <= 0) return '00:00:00';

	const hours = Math.floor(diff / (1000 * 60 * 60));
	const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((diff % (1000 * 60)) / 1000);

	const hh = String(hours).padStart(2, '0');
	const mm = String(minutes).padStart(2, '0');
	const ss = String(seconds).padStart(2, '0');
	return `${hh}:${mm}:${ss}`;
}

function hashSeed(input) {
	let hash = 2166136261;
	for (let i = 0; i < input.length; i += 1) {
		hash ^= input.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

function mulberry32(seed) {
	let value = seed;
	return () => {
		value += 0x6d2b79f5;
		let t = value;
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function seededShuffle(items, seed) {
	const array = [...items];
	const rng = mulberry32(seed);
	for (let i = array.length - 1; i > 0; i -= 1) {
		const j = Math.floor(rng() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
	return array;
}

const ratingLevels = [
	{ score: 0, label: 'Popcorn Spiller', emoji: '🍿', description: 'Every classic starts somewhere.' },
	{ score: 1, label: 'Trailer Watcher', emoji: '🎬', description: 'You can smell the opening credits.' },
	{ score: 2, label: 'Matinee Regular', emoji: '🎟️', description: 'Solid instincts, keep rolling.' },
	{ score: 3, label: 'Couch Critic', emoji: '🛋️', description: 'You know your lines and your lore.' },
	{ score: 4, label: 'Quote Whisperer', emoji: '✨', description: 'Nearly telepathic with movie quotes.' },
	{ score: 5, label: 'Cinematic Oracle', emoji: '🔮', description: 'You hear the quote before it plays.' },
];

function getRatingForScore(score) {
	return ratingLevels.find((entry) => entry.score === score) || {
		label: 'Movie Explorer',
		emoji: '🎥',
		description: 'Keep exploring the cinematic universe.',
	};
}

function DailyFiveMode({
	allMovieTitles,
	handleTwitterShare,
	handleFacebookShare,
	handleCopyLink,
	copied,
}) {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [dailyQuotes, setDailyQuotes] = useState([]);
	const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
	const [highestIndex, setHighestIndex] = useState(0);
	const [userGuess, setUserGuess] = useState('');
	const [attempts, setAttempts] = useState([]);
	const [gameOver, setGameOver] = useState(false);
	const [score, setScore] = useState(0);
	const [shareMessage, setShareMessage] = useState('');
	const [shareButtonText, setShareButtonText] = useState('Share Results');
	const [searchResults, setSearchResults] = useState([]);
	const [showDropdown, setShowDropdown] = useState(false);
	const [timeLeft, setTimeLeft] = useState(getTimeUntilMidnight());

	const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

	async function ensureAuth() {
		const auth = getAuth();
		try {
			await setPersistence(auth, browserLocalPersistence);
			if (!auth.currentUser) {
				await signInAnonymously(auth);
			}
		} catch (error) {
			console.error('Error during authentication:', error);
		}
	}

	useEffect(() => {
		ensureAuth();
	}, []);

	useEffect(() => {
		const lastPlayedDate = localStorage.getItem(DAILY_FIVE_KEY);
		if (lastPlayedDate === today) {
			setGameOver(true);
			setHighestIndex(4);
			const savedMessage = localStorage.getItem(DAILY_FIVE_SHARE_KEY);
			const savedAttempts = localStorage.getItem(DAILY_FIVE_ATTEMPTS_KEY);
			const savedScore = localStorage.getItem(DAILY_FIVE_SCORE_KEY);
			if (savedMessage) setShareMessage(JSON.parse(savedMessage));
			if (savedAttempts) setAttempts(JSON.parse(savedAttempts));
			if (savedScore) setScore(JSON.parse(savedScore));
		}
	}, [today]);

	useEffect(() => {
		const timer = setInterval(() => {
			setTimeLeft(getTimeUntilMidnight());
		}, 1000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (searchResults.length > 0) {
			setShowDropdown(true);
		} else {
			setShowDropdown(false);
		}
	}, [searchResults]);

	useEffect(() => {
		const fetchDailyFiveQuotes = async () => {
			try {
				const auth = getAuth();
				if (!auth.currentUser) {
					await ensureAuth();
				}

				const dailyRef = doc(db, 'daily_five_quotes', today);
				const playCountsRef = doc(db, 'daily_five_quotes', 'playCounts');
				const dailyDoc = await getDoc(dailyRef);

				const hydrateDailySet = async (selectionData) => {
					const movieDocs = await Promise.all(
						selectionData.selections.map((selection) => getDoc(doc(db, 'movies', selection.movieId)))
					);

					const hydrated = movieDocs
						.map((movieDoc, index) => {
							if (!movieDoc.exists()) return null;
							const movieData = movieDoc.data();
							const quoteIndex = selectionData.selections[index].quoteIndex;
							const quote = movieData.quotes?.[quoteIndex];
							if (!quote) return null;
							return {
								id: movieDoc.id,
								title: movieData.title,
								series: movieData.series || [],
								quoteIndex,
								quote,
							};
						})
						.filter(Boolean);

					if (hydrated.length !== 5) {
						console.error('Daily five quote selection is incomplete.');
					}

					setDailyQuotes(hydrated);
				};

				if (dailyDoc.exists()) {
					await hydrateDailySet(dailyDoc.data());
					return;
				}

				const moviesSnapshot = await getDocs(collection(db, 'movies'));
				if (moviesSnapshot.empty) {
					console.error('No movies found in the movies collection.');
					return;
				}

				const allMovies = moviesSnapshot.docs
					.map((movieDoc) => ({ id: movieDoc.id, ...movieDoc.data() }))
					.filter((movie) => Array.isArray(movie.quotes) && movie.quotes.length > 0);

				if (allMovies.length < 5) {
					console.error('Not enough movies with quotes to build the daily five.');
					return;
				}

				let existingData = null;
				let selections = null;

				await runTransaction(db, async (transaction) => {
					const dailyDocCheck = await transaction.get(dailyRef);
					if (dailyDocCheck.exists()) {
						existingData = dailyDocCheck.data();
						return;
					}

					// Read play counts INSIDE transaction to ensure fresh data
					const currentPlayCountsDoc = await transaction.get(playCountsRef);
					const playCounts = currentPlayCountsDoc.exists()
						? { ...currentPlayCountsDoc.data().counts }
						: {};

					// Perform selection INSIDE transaction using fresh play counts
					const seedNumber = parseInt(today.split('-').join(''), 10);
					const countBuckets = new Map();
					allMovies.forEach((movie) => {
						const count = playCounts[movie.id] || 0;
						if (!countBuckets.has(count)) {
							countBuckets.set(count, []);
						}
						countBuckets.get(count).push(movie);
					});

					const sortedCounts = Array.from(countBuckets.keys()).sort((a, b) => a - b);
					const orderedMovies = [];
					sortedCounts.forEach((count) => {
						const bucketSeed = hashSeed(`${today}-${count}`);
						const bucket = seededShuffle(countBuckets.get(count), bucketSeed);
						orderedMovies.push(...bucket);
					});

					selections = [];
					let i = 0;
					while (selections.length < 5 && i < orderedMovies.length) {
						const movie = orderedMovies[i];
						if (!selections.find((entry) => entry.movieId === movie.id)) {
							const quoteIndex = (seedNumber + i * 7 + movie.title.length) % movie.quotes.length;
							selections.push({
								movieId: movie.id,
								movieTitle: movie.title,
								quoteIndex,
							});
						}
						i += 1;
					}

					// Update play counts with selections
					selections.forEach((selection) => {
						playCounts[selection.movieId] = (playCounts[selection.movieId] || 0) + 1;
					});

					transaction.set(dailyRef, {
						selections,
						selectedAt: new Date().toISOString(),
					});

					if (currentPlayCountsDoc.exists()) {
						transaction.update(playCountsRef, { counts: playCounts });
					} else {
						transaction.set(playCountsRef, { counts: playCounts });
					}
				});

				const selectionData = existingData || { selections };
				await hydrateDailySet(selectionData);
			} catch (error) {
				console.error('Error fetching daily five quotes:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchDailyFiveQuotes();
	}, [today]);

	const handleInputChange = (event) => {
		const input = event.target.value;
		setUserGuess(input);

		if (input.length > 0) {
			const filteredResults = allMovieTitles
				.filter((title) => title.toLowerCase().includes(input.toLowerCase()))
				.sort((a, b) => {
					const aStartsWith = a.toLowerCase().startsWith(input.toLowerCase());
					const bStartsWith = b.toLowerCase().startsWith(input.toLowerCase());
					if (aStartsWith && !bStartsWith) return -1;
					if (!aStartsWith && bStartsWith) return 1;
					return 0;
				});

			setSearchResults(filteredResults);
		} else {
			setSearchResults([]);
		}
	};

	const handleDropdownSelection = (result) => {
		setUserGuess(result);
		setShowDropdown(false);
	};

	const handleShareResults = () => {
		const message = shareMessage || generateShareMessage(attempts, score);
		navigator.clipboard.writeText(message).then(() => {
			setShareButtonText('Copied!');
			setTimeout(() => setShareButtonText('Share Results'), 2000);
		});
	};

	const handleGuess = () => {
		if (gameOver || dailyQuotes.length === 0) return;

		const attemptText = userGuess.trim() === '' ? 'Skipped' : userGuess;
		const currentRound = dailyQuotes[currentQuoteIndex];
		const actual = currentRound.title?.toLowerCase() || '';
		const isCorrect = attemptText.toLowerCase() === actual;
		const isSeriesMatch = currentRound.series && currentRound.series.includes(attemptText);

		const newAttempts = [
			...attempts,
			{
				guess: attemptText,
				isCorrect,
				isSeriesMatch,
				movieTitle: currentRound.title,
			},
		];

		setAttempts(newAttempts);

		if (currentQuoteIndex >= dailyQuotes.length - 1) {
			const newScore = newAttempts.filter((attempt) => attempt.isCorrect).length;
			setScore(newScore);
			setGameOver(true);
			setHighestIndex(dailyQuotes.length - 1);
			handleGameOver(newAttempts, newScore);
			return;
		}

		const nextIndex = currentQuoteIndex + 1;
		setHighestIndex(nextIndex);
		setCurrentQuoteIndex(nextIndex);
		setUserGuess('');
	};

	const handleGameOver = async (attemptsArray, newScore) => {
		const message = generateShareMessage(attemptsArray, newScore);
		setShareMessage(message);
		localStorage.setItem(DAILY_FIVE_KEY, today);
		localStorage.setItem(DAILY_FIVE_SHARE_KEY, JSON.stringify(message));
		localStorage.setItem(DAILY_FIVE_ATTEMPTS_KEY, JSON.stringify(attemptsArray));
		localStorage.setItem(DAILY_FIVE_SCORE_KEY, JSON.stringify(newScore));

		try {
			await recordDailyFiveResult(newScore);
		} catch (error) {
			console.error('Failed to record daily five result:', error);
		}
	};

	async function recordDailyFiveResult(scoreValue) {
		const auth = getAuth();
		const statsRef = doc(db, 'daily_five_stats', today);
		try {
			if (!auth.currentUser) {
				await signInAnonymously(auth);
			}
			const userId = auth.currentUser.uid;
			const userStatsRef = doc(db, 'user_stats', userId);

			await runTransaction(db, async (transaction) => {
				const statsSnap = await transaction.get(statsRef);
				const userSnap = await transaction.get(userStatsRef);
				const userData = userSnap.exists() ? userSnap.data() : null;
				const hasPlayedToday = Boolean(userData?.dailyFive?.datesPlayed?.[today]);

				if (!statsSnap.exists()) {
					transaction.set(statsRef, {
						totalGamesPlayed: 1,
						totalUniquePlayers: hasPlayedToday ? 0 : 1,
						scoreDistribution: {
							0: scoreValue === 0 ? 1 : 0,
							1: scoreValue === 1 ? 1 : 0,
							2: scoreValue === 2 ? 1 : 0,
							3: scoreValue === 3 ? 1 : 0,
							4: scoreValue === 4 ? 1 : 0,
							5: scoreValue === 5 ? 1 : 0,
						},
					});
				} else {
					const updates = {
						totalGamesPlayed: increment(1),
						[`scoreDistribution.${scoreValue}`]: increment(1),
					};

					if (!hasPlayedToday) {
						updates.totalUniquePlayers = increment(1);
					}

					transaction.update(statsRef, updates);
				}

				if (!userSnap.exists()) {
					transaction.set(userStatsRef, {
						userId,
						dailyFive: {
							gamesPlayed: 1,
							lastPlayed: today,
							datesPlayed: {
								[today]: 1,
							},
						},
					});
				} else {
					transaction.update(userStatsRef, {
						'dailyFive.gamesPlayed': increment(1),
						'dailyFive.lastPlayed': today,
						[`dailyFive.datesPlayed.${today}`]: increment(1),
					});
				}
			});
		} catch (error) {
			console.error('Error updating daily five stats:', error);
		}
	}

	const generateShareMessage = (attemptsArray, scoreValue) => {
		const resultIcons = Array(dailyQuotes.length).fill('⬜');
		attemptsArray.forEach((attempt, index) => {
			if (attempt.isCorrect) {
				resultIcons[index] = '🟩';
			} else if (attempt.isSeriesMatch) {
				resultIcons[index] = '🟦';
			} else {
				resultIcons[index] = '🟥';
			}
		});

		const rating = getRatingForScore(scoreValue);
		return `Movie Quotes Guesser - Daily 5 - ${today}\nScore: ${scoreValue}/5 - ${rating.label}\n📜 ${resultIcons.join(
			' '
		)}\nhttps://moviequotesguesser.com/daily-five`;
	};

	if (isLoading) {
		return (
			<div className="loading-container">
				<div className="spinner"></div>
			</div>
		);
	}

	if (dailyQuotes.length === 0) {
		return (
			<div className="no-daily-movie-container">
				<div className="no-daily-movie-card">
					<h2 className="no-daily-movie-title">Hang Tight!</h2>
					<p className="no-daily-movie-text">
						We are working hard to make today&apos;s Daily Five. Please come back soon.
					</p>
				</div>
			</div>
		);
	}

	const activeQuote = dailyQuotes[currentQuoteIndex];
	const rating = getRatingForScore(score);

	return (
		<div className="light-mode">
			<div className="header-container">
				<div className="header-left">
					<SideMenu />
					<img
						src={triviaIcon}
						alt="Movie Quotes Guesser Trivia Icon"
						className="trivia-icon"
						onClick={() => navigate('/trivia')}
					/>
				</div>
				<div className="logo-box" onClick={() => navigate('/')}>
					<img src={logo} alt="Movie Quotes Guesser Logo" className="logo" />
					<h3 className="logo-text">Movie Quotes Guesser</h3>
				</div>
				<div className="right-section">
					<div className="mode-switch-group">
						<button className="mode-switch" onClick={() => navigate('/')}>
							Classic Daily
						</button>
						<button className="mode-switch active" onClick={() => navigate('/daily-five')}>
							Daily Five
							<span className="mode-switch-badge">New</span>
						</button>
					</div>
					<div className="challenge-container">
						<img
							src={challengeIcon}
							alt="Challenge Icon"
							className="challenge-icon"
							onClick={() => navigate('/challenge')}
						/>
					</div>
					<img
						src={leaderboardIcon}
						alt="Leaderboard Icon"
						className="leaderboard-icon"
						onClick={() => navigate('/leaderboard/Nolan')}
					/>
					<img src={statsIcon} alt="Stats Icon" className="stats-icon" onClick={() => navigate('/stats')} />
				</div>
			</div>

			<div className="game-container">
				<div className="poster-container">
					<img src={activeQuote.quote.sketch} alt="Sketch for the daily five quote" className="poster-image" />
				</div>
				<main className="main-content">
					{gameOver ? (
						<div className="game-over">
							<div className="progress-banner-container">
								<div className="progress-bar">
									{dailyQuotes.map((_, index) => {
										const isActive = index === currentQuoteIndex;
										const isVisible = index <= highestIndex;
										const isSeriesMatch = attempts[index] && attempts[index].isSeriesMatch;
										const isCorrect = attempts[index] && attempts[index].isCorrect;
										return (
											<div
												key={index}
												className={`progress-block ${
													isActive
														? 'active'
														: isCorrect
														? 'success'
														: isSeriesMatch
														? 'series-match'
														: isVisible
														? 'completed'
														: ''
												}`}
												onClick={() => setCurrentQuoteIndex(index)}
											/>
										);
									})}
								</div>
							</div>

							<blockquote className="quote-text">{activeQuote.quote.text}</blockquote>
							<div className="audio-box">
								<audio
									controls
									src={activeQuote.quote.audio}
									className="audio-player"
									controlsList="nodownload noplaybackrate"
								/>
							</div>

							<h2 className="status-message">
								{score >= 3 ? (
									<>
										<img src={partyIcon} alt="Party Icon" className="game-status-icon" /> Nice run!
									</>
								) : (
									<>
										<img src={sadIcon} alt="Sad Icon" className="game-status-icon" /> Daily Five complete!
									</>
								)}
							</h2>
							<div className="daily-five-score">
								<div className="daily-five-score-value">Score: {score}/5</div>
								<div className="daily-five-score-rating">
									<span className="daily-five-score-emoji">{rating.emoji}</span>
									<span className="daily-five-score-label">{rating.label}</span>
								</div>
								<div className="daily-five-score-description">{rating.description}</div>
							</div>
							<div className="daily-five-answers">
								{dailyQuotes.map((quote, index) => {
									const wasCorrect = attempts[index]?.isCorrect;
									return (
										<div key={quote.id} className={`daily-five-answer ${wasCorrect ? 'correct' : 'wrong'}`}>
											{wasCorrect ? '✓' : '✗'} {quote.title}
										</div>
									);
								})}
							</div>
							<h3>Come back for a new Daily Five in: {timeLeft}</h3>
							<div className="game-over-buttons">
								<button className="share-result-button" onClick={handleShareResults}>
									{shareButtonText}
								</button>
							</div>
						</div>
					) : (
						<div className="game-active">
							<div className="progress-bar">
								{dailyQuotes.map((_, index) => {
									const isActive = index === currentQuoteIndex;
									const isVisible = index <= highestIndex;
									const isSeriesMatch = attempts[index] && attempts[index].isSeriesMatch;
									const isCorrect = attempts[index] && attempts[index].isCorrect;
									return (
										<div
											key={index}
											className={`progress-block ${
												isActive
													? 'active'
													: isCorrect
													? 'success'
													: isSeriesMatch
													? 'series-match'
													: isVisible
													? 'completed'
													: ''
											}`}
										/>
									);
								})}
							</div>
							<blockquote className="quote-text">{activeQuote.quote.text}</blockquote>
							<div className="audio-box flex-start-on-mobile">
								<audio
									controls
									src={activeQuote.quote.audio}
									className="audio-player wider-on-mobile"
									controlsList="nodownload noplaybackrate"
								/>
							</div>

							<div className="input-group-box flex-start-on-mobile">
								<div className="input-group wider-on-mobile">
									<input
										type="text"
										className="input-text"
										value={userGuess}
										onChange={handleInputChange}
										placeholder="Guess the movie title or click Submit to skip..."
										onFocus={() => setShowDropdown(true)}
										disabled={gameOver}
									/>
									<button className="btn-primary" onClick={handleGuess} disabled={gameOver}>
										Submit
									</button>
									{showDropdown && searchResults.length > 0 && (
										<ul className="search-results">
											{searchResults.map((result, index) => (
												<li
													key={index}
													className="search-result-item"
													onClick={() => handleDropdownSelection(result)}
												>
													{result}
												</li>
											))}
										</ul>
									)}
								</div>
							</div>

							<div className="guess-container">
								<div className="previous-guesses">
									{attempts.map((attemptObj, index) => (
										<div key={index} className="guess-item">
											{attemptObj.isCorrect ? '✓' : '✗'} {attemptObj.guess}
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</main>
			</div>

			<div className="footer-container">
				<div className="share-buttons">
					<img src={xIcon} alt="Share us on X" className="share-image" onClick={handleTwitterShare} />

					<img
						src={facebookIcon}
						alt="Share us on Facebook"
						className="share-image"
						onClick={handleFacebookShare}
					/>

					<img src={linkIcon} alt="Copy our website link" className="share-image" onClick={handleCopyLink} />
					{copied && <span className="copied-text">Copied</span>}
				</div>
				<FooterLinks />

				<a
					href="https://buymeacoffee.com/moviequotesguesser"
					target="_blank"
					rel="noopener noreferrer"
					className="support-button"
				>
					<img src={coffeeIcon} alt="Buy Me A Coffee" className="support-image" />
				</a>
			</div>
		</div>
	);
}

export default DailyFiveMode;
